import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { IMAGES, SALES_POINTS, PIPELINE_STAGES } from "@/data/content";
import { Reveal } from "@/components/Reveal";
import { Eyebrow, GoldButton } from "@/components/Section";
import { Fragment } from "@/components/Decor";

const PipelineDiagram = () => (
  <div data-testid="pipeline-diagram" className="glass rounded-[28px] p-6 lg:p-8">
    <div className="flex items-center justify-between">
      <p className="font-mono-brand text-[10px] tracking-[0.25em] uppercase text-[#6B7A8F]">Sales Pipeline · Ενδεικτική Δομή</p>
      <span className="h-2 w-2 rounded-full bg-[#5A7265]" />
    </div>
    <div className="mt-6 grid grid-cols-6 gap-2">
      {PIPELINE_STAGES.map((s, i) => (
        <div key={s} className="flex flex-col items-center gap-2">
          <div
            className="w-full rounded-xl border bg-white/80 transition-transform duration-500 hover:-translate-y-1"
            style={{
              height: 26 + (5 - i) * 10,
              borderColor: i < 2 ? "#C26D5266" : i < 4 ? "#2C4A6F55" : "#5A726566",
              background: `linear-gradient(180deg, #ffffff, ${i < 2 ? "#F7EFE9" : i < 4 ? "#EEF2F7" : "#EAF0EC"})`,
            }}
          />
          <span className="text-[9px] sm:text-[10px] font-medium text-[#3B4A5E] text-center leading-tight">{s}</span>
        </div>
      ))}
    </div>
    <div className="mt-6 flex items-center gap-3 rounded-2xl bg-[#0E1B31] px-4 py-3 text-white">
      <span className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-[#C5A059]">CRM</span>
      <span className="h-4 w-px bg-white/20" />
      <p className="text-[11px] text-white/80">Κάθε ευκαιρία έχει υπεύθυνο, επόμενο βήμα και ημερομηνία follow-up.</p>
    </div>
  </div>
);

export const SalesSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section ref={ref} data-testid="sales-section" className="relative overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-[-10%] -z-0">
        <img src={IMAGES.commercial} alt="" className="h-full w-full object-cover" loading="lazy" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0E1B31] via-[#0E1B31]/92 to-[#16294A]/70" />
      <div className="absolute inset-0 mesh-navy opacity-60 mix-blend-multiply" />
      <div className="perspective-grid light" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <Reveal className="lg:col-span-6">
          <Eyebrow tone="#E39C82">Εμπορική Ανάπτυξη</Eyebrow>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-white">
            Περισσότερες πωλήσεις, <span className="italic text-[#E39C82]">με σύστημα.</span>
          </h2>
          <p className="mt-7 text-base lg:text-lg leading-relaxed text-white/75 max-w-xl">
            Η ανάπτυξη δεν έρχεται από ευκαιρίες που εμφανίζονται τυχαία. Έρχεται από μια οργανωμένη
            εμπορική λειτουργία: ξεκάθαρους στόχους, δομημένο pipeline και συστηματικό follow-up.
          </p>
          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
            {SALES_POINTS.map((item) => (
              <li key={item.title} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#C26D52]/20 text-[#E39C82]">
                  <Check className="h-3 w-3" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-white">{item.title}</p>
                  <p className="mt-0.5 text-[13px] leading-relaxed text-white/65">{item.desc}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-12">
            <GoldButton to="/services#commercial" testId="sales-section-cta" variant="copper">
              Δείτε πώς μπορούμε να βοηθήσουμε
            </GoldButton>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-6 relative">
          <div className="relative lg:pl-8">
            <PipelineDiagram />
            <Fragment testId="sales-fragment" className="absolute -top-6 right-0 lg:-right-4 w-[220px] float-mid">
              <p className="font-mono-brand text-[9px] tracking-[0.25em] uppercase text-[#6B7A8F]">Follow-up</p>
              <p className="mt-1.5 text-[12px] leading-snug text-[#0E1B31]">
                Καμία ευκαιρία δεν μένει χωρίς επόμενο βήμα.
              </p>
            </Fragment>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
